import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tabs, Tab, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  closeTab,
  setActiveTab,
  setActiveSubTab,
  updateTabLabel,
  requestTabSave
} from '../../modules/auth/tabsSlice';
import { useTabComponents, clearTabComponent } from '../../hooks/useTab';
import PageCloseModal from '../../components/shared-components/page-close-modal';
import { axiosInstance } from '../../api/config/axiosInstance';

import { useTranslate } from 'src/locales';

const TabPanel = () => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const tabs = useSelector((state) => state.tabs.mainTabs);
  const activeTabIndex = useSelector((state) => state.tabs.activeTabIndex);
  const shouldUpdateUrl = useSelector((state) => state.tabs.shouldUpdateUrl);

  // Modal state for close confirmation
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [tabToClose, setTabToClose] = useState(null);
  const [modalVariant, setModalVariant] = useState('full'); // 'one' veya 'full'
  const [tabNameToShow, setTabNameToShow] = useState(''); // Modal'da gösterilecek tab adı
  const [modalTitle, setModalTitle] = useState('');
  const [yesButtonText, setYesButtonText] = useState('');
  const [noButtonText, setNoButtonText] = useState('');
  const [onDenyAction, setOnDenyAction] = useState(null);
  const [onConfirmAction, setOnConfirmAction] = useState(null);

  // Patient names mapping
  const [patientNames, setPatientNames] = useState({});

  // Get all component IDs first - BEFORE any early returns
  const allComponentIds = useMemo(() => {
    const ids = [];
    tabs.forEach(tab => {
      if (tab.subTabs && tab.subTabs.length > 0) {
        // Patient tabs with subtabs
        tab.subTabs.forEach(subTab => {
          ids.push(subTab.componentId);
        });
      } else {
        // Standalone tabs
        ids.push(tab.componentId);
      }
    });
    return ids;
  }, [tabs]);

  // Resolve tab components without an arbitrary limit (fixes blank screens when many tabs are open)
  const allComponents = useTabComponents(allComponentIds);

  const activeTab = tabs[activeTabIndex];

  // Fetch patient names for tabs with patient IDs (only if not already provided)
  useEffect(() => {
    const fetchPatientNames = async () => {
      for (const tab of tabs) {
        // Skip if tab already has patient name in data
        if (tab.data?.patientName) {
          // Update label if it's still showing "Hasta {id}"
          if (tab.label.startsWith('Hasta ')) {
            dispatch(updateTabLabel({
              id: tab.id,
              label: tab.data.patientName
            }));
          }
          continue;
        }

        // Check if tab has patient ID and label starts with "Hasta"
        if (tab.data?.patientId && tab.label.startsWith('Hasta ')) {
          const patientId = tab.data.patientId;

          // Skip if already fetched
          if (patientNames[patientId]) continue;

          try {
            const response = await axiosInstance.get(
              `${process.env.REACT_APP_API_URL}/nurse/api/Patient/GetPatientInfo/${patientId}`
            );

            if (response.status === 200 && response.data?.data) {
              const { name, surname } = response.data.data;
              const fullName = `${name || ''} ${surname || ''}`.trim();

              if (fullName) {
                // Update state
                setPatientNames(prev => ({ ...prev, [patientId]: fullName }));

                // Update tab label in Redux
                dispatch(updateTabLabel({
                  id: tab.id,
                  label: fullName
                }));
              }
            }
          } catch (error) {
            console.error(`Error fetching patient ${patientId} name:`, error);
          }
        }
      }
    };

    fetchPatientNames();
  }, [tabs, dispatch, patientNames]);

  // activeComponentId kaldırıldı - artık allComponents kullanılıyor

  // currentComponent kaldırıldı - artık allComponents kullanılıyor

  // Eğer hiç tab yoksa boş bir mesaj göster - AFTER all hooks
  if (tabs.length === 0) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Hiç açık sekme yok</div>
      </Box>
    );
  }

  const updateUrl = (path) => {
    if (shouldUpdateUrl && window.history?.pushState && path) {
      // Normal URL'ler oluştur (hash olmadan)
      let fullUrl = path.startsWith('/') ? path : `/${path}`;

      // 404 kontrolü - eğer path geçersizse güncelleme yapma
      if (fullUrl.includes('404') || fullUrl.includes('undefined')) {
        return;
      }

      // Path temizleme işlemleri
      fullUrl = fullUrl
        .replace(/\/+/g, '/')
        .replace(/^\/+|\/+$/g, '');

      // Başına / ekle
      if (!fullUrl.startsWith('/')) {
        fullUrl = '/' + fullUrl;
      }

      window.history.replaceState({}, '', fullUrl);
    }
  };

  const handleMainTabChange = (event, newValue) => {
    if (newValue >= 0 && newValue < tabs.length) {
      dispatch(setActiveTab(newValue));
    }
  };

  const handleTabClose = (event, tabId) => {
    event.stopPropagation();

    // Hangi tab kapatılıyor - ana tab mı, alt tab mı?
    const mainTab = tabs.find(t => t.id === tabId);
    let isMainTab = false;
    let isSubTab = false;
    let tabName = '';
    let variant = 'full';

    if (mainTab) {
      // Ana tab kapatılıyor
      isMainTab = true;
      tabName = mainTab.label;

      // Eğer altında sub tab'lar varsa → full, yoksa → one
      if (mainTab.subTabs && mainTab.subTabs.length > 0) {
        variant = 'full';
      } else {
        variant = 'one';
      }
    } else {
      // Alt tab kapatılıyor olabilir
      for (const tab of tabs) {
        if (tab.subTabs) {
          const subTab = tab.subTabs.find(st => st.id === tabId);
          if (subTab) {
            isSubTab = true;
            tabName = subTab.label;
            variant = 'one'; // Alt tab için daima 'one'
            break;
          }
        }
      }
    }

    // Modal'ı aç
    setTabToClose(tabId);
    setModalVariant(variant);
    setTabNameToShow(tabName);

    // Check dirty state - DISABLED as per user request
    let targetTab = null;
    let isDirty = false;

    /*
    if (isMainTab) {
      targetTab = mainTab;
      // Check if main tab itself is dirty OR any of its subtabs are dirty
      if (mainTab.isDirty) {
        isDirty = true;
      } else if (mainTab.subTabs && mainTab.subTabs.length > 0) {
        const dirtySubTab = mainTab.subTabs.find(st => st.isDirty);
        if (dirtySubTab) {
          isDirty = true;
          // If we found a dirty subtab, we might want to switch to it or just warn
          // For now, just mark as dirty so modal shows up
        }
      }
    } else if (isSubTab) {
      // Find the subtab
      for (const t of tabs) {
        if (t.subTabs) {
          const found = t.subTabs.find(st => st.id === tabId);
          if (found) {
            targetTab = found;
            isDirty = !!found.isDirty;
            break;
          }
        }
      }
    }
    */

    console.log('❌ closing tab request:', {
      tabId,
      isMainTab,
      isSubTab,
      targetTabLabel: targetTab?.label,
      targetTabDirty: targetTab?.isDirty,
      calculatedIsDirty: isDirty
    });

    if (isDirty) {
      setModalTitle(t('page-close.unsaved-changes-title') || "Kaydedilmemiş değişiklikler mevcut. Kaydedilmeden kapatmak istiyor musunuz?");
      setYesButtonText(t('page-close.save-and-close') || "Kaydet ve Kapat");
      setNoButtonText(t('page-close.close-without-saving') || "Kaydetmeden Kapat");

      // On Deny (Hayır): Close without saving
      setOnDenyAction(() => () => {
        confirmTabClose();
      });

      // On Confirm (Evet): Save and Close
      setOnConfirmAction(() => () => {
        dispatch(requestTabSave({ id: tabId, requestId: Date.now().toString(), reason: 'close' }));
        setCloseModalOpen(false);
        // Note: Actual closing is handled by the component after successful save, or we rely on the component to close itself. 
        // But if save fails, tab stays open. This is desired.
      });

    } else {
      setModalTitle('');
      setYesButtonText('');
      setNoButtonText('');
      setOnDenyAction(null);
      setOnConfirmAction(null);
    }

    setCloseModalOpen(true);
  };

  const confirmTabClose = () => {
    if (!tabToClose) return;

    // Find the tab to get its componentId before closing
    const tab = tabs.find(t => t.id === tabToClose);

    if (tab?.componentId) {
      clearTabComponent(tab.componentId);
    }

    // Also clear any subtab components
    if (tab?.subTabs) {
      tab.subTabs.forEach(subTab => {
        if (subTab.componentId) {
          clearTabComponent(subTab.componentId);
        }
      });
    }

    // Check if it's a subtab (not found in main tabs)
    if (!tab) {
      // Look for subtab
      tabs.forEach(mainTab => {
        if (mainTab.subTabs) {
          const subTab = mainTab.subTabs.find(st => st.id === tabToClose);
          if (subTab?.componentId) {
            clearTabComponent(subTab.componentId);
          }
        }
      });
    }

    dispatch(closeTab(tabToClose));

    // Reset modal state
    setTabToClose(null);
    setModalTitle('');
    setYesButtonText('');
    setNoButtonText('');
    setOnDenyAction(null);
    setOnConfirmAction(null);
  };

  const handleSubTabChange = (mainIndex, subIndex) => {
    dispatch(setActiveSubTab({ mainIndex, subIndex }));
    const mainTab = tabs[mainIndex];
    if (mainTab?.subTabs?.[subIndex]?.path) {
      // Subtab path'ini URL'ye güncelle - query parametreleriyle birlikte
      const subTabPath = mainTab.subTabs[subIndex].path;
      updateUrl(subTabPath);
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Main Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTabIndex}
          onChange={handleMainTabChange}
          variant="scrollable"
          scrollButtons="auto"
          component={Box}
          sx={{
            minHeight: '52px',
            height: '52px',
            '& .MuiTabs-indicator': {
              backgroundColor: '#2196F3',
              height: '3px',
            },
            '& .MuiTabs-flexContainer': {
              gap: 0,
            },
            '& .MuiTab-root': {
              padding: 0,
              minHeight: '52px',
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.id}
              value={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <span>{tab.label}</span>
                  {/* Show close button only for active tab */}
                  {activeTabIndex === index && (
                    <Box
                      component="span"
                      className="close-button"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleTabClose(e, tab.id);
                      }}
                      sx={{
                        ml: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </Box>
                  )}
                </Box>
              }
              sx={{
                width: '228px !important',
                height: '52px !important',
                minWidth: '228px !important',
                minHeight: '52px !important',
                maxWidth: '228px !important',
                maxHeight: '52px !important',
                padding: '0 16px !important',
                margin: '0 !important',
                border: 'none !important',
                borderRadius: '0 !important',
                bgcolor: 'transparent',
                color: activeTabIndex === index ? 'text.primary' : 'text.secondary',
                '&:hover': {
                  bgcolor: 'transparent',
                  color: '#2196F3',
                  '& .close-button': {
                    color: '#2196F3',
                  }
                },
                transition: 'color 0.3s ease',
                '&.Mui-selected': {
                  bgcolor: 'transparent',
                },
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Subtabs - Only show if the active tab has subtabs */}
      {activeTab?.subTabs?.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', ml: 2 }}>
          <Tabs
            value={activeTab.activeSubTab || 0}
            onChange={(e, value) => handleSubTabChange(activeTabIndex, value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#2196F3',
                height: '3px',
              },
              '& .MuiTabs-flexContainer': {
                gap: 0,
              },
              '& .MuiTab-root': {
                padding: 0,
                minHeight: '48px',
              }
            }}
          >
            {activeTab.subTabs.map((subTab, index) => {
              const isActive = (activeTab.activeSubTab || 0) === index;
              return (
                <Tab
                  key={subTab.id}
                  value={index}
                  label={
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      <span>{subTab.label}</span>
                      {/* Show close button for all subtabs */}
                      <Box
                        component="span"
                        className="close-button"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleTabClose(e, subTab.id);
                        }}
                        sx={{
                          ml: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          color: isActive ? '#2196F3' : 'inherit',
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </Box>
                    </Box>
                  }
                  sx={{
                    margin: '0 !important',
                    border: 'none !important',
                    borderRadius: '0 !important',
                    padding: '0 16px !important',
                    bgcolor: 'transparent',
                    color: isActive ? '#2196F3' : 'text.secondary',
                    '&:hover': {
                      bgcolor: 'transparent',
                      color: '#2196F3',
                      '& .close-button': {
                        color: '#2196F3',
                      }
                    },
                    transition: 'color 0.3s ease',
                    '&.Mui-selected': {
                      bgcolor: 'transparent',
                      color: '#2196F3',
                    },
                  }}
                />
              );
            })}
          </Tabs>
        </Box>
      )}

      {/* Content - All tabs rendered but hidden/shown with display */}
      <Box sx={{ p: 3, flexGrow: 1, overflow: 'visible' }}>
        {tabs.map((tab, tabIndex) => {
          if (tab.subTabs && tab.subTabs.length > 0) {
            // Patient tabs with subtabs
            return tab.subTabs.map((subTab, subIndex) => {
              const isActive = tabIndex === activeTabIndex && subIndex === (tab.activeSubTab || 0);
              const component = allComponents[subTab.componentId]; // Use pre-fetched component

              return (
                <Box
                  key={`${tab.id}-${subTab.id}`}
                  sx={{
                    display: isActive ? 'block' : 'none',
                  }}
                >
                  {React.isValidElement(component) ? React.cloneElement(component, { tabId: subTab.id }) : component}
                </Box>
              );
            });
          } else {
            // Standalone tabs
            const isActive = tabIndex === activeTabIndex;
            const component = allComponents[tab.componentId]; // Use pre-fetched component

            return (
              <Box
                key={tab.id}
                sx={{
                  display: isActive ? 'block' : 'none',
                }}
              >
                {React.isValidElement(component) ? React.cloneElement(component, { tabId: tab.id }) : component}
              </Box>
            );
          }
        })}
      </Box>

      {/* Page Close Confirmation Modal */}
      <PageCloseModal
        open={closeModalOpen}
        onClose={() => {
          setCloseModalOpen(false);
          setTabToClose(null);
          setTabNameToShow('');
          setModalTitle('');
          setYesButtonText('');
          setNoButtonText('');
          setOnDenyAction(null);
          setOnConfirmAction(null);
        }}
        onConfirm={onConfirmAction ? onConfirmAction : confirmTabClose}
        variant={modalVariant}
        tabName={tabNameToShow}
        title={modalTitle}
        yesButtonText={yesButtonText}
        noButtonText={noButtonText}
        onDeny={onDenyAction}
      />
    </Box>
  );
};

export default TabPanel;
